import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { ProductPageContent } from '@/backend/models/productPageContent';
import { ProductPageSection } from '@/backend/models/productPageSection';
import { NotFound } from '@/frontend/pages/NotFound';
import { useProductPageStore } from '@/frontend/store/ProductPageStore';
import { I18n } from '@/i18n/I18n';
import { useProcedureInstanceStore } from '@/frontend/store/ProcedureInstanceStore';
import { ContainersPlaceholder } from '../containers/ContainersPlaceholder';
import { FileWithDisplay } from '../files/FileWithDisplay';
import { Breadcrumbs } from '../Breadcrumbs';
import { ProductPageSectionType } from '@/backend/access/productPageSection/types';
import { Pencil, Settings2 } from 'lucide-react';
import { useProcedureListStore } from '@/frontend/store/ProcedureListStore';
import { ProjectBreadcrumb } from '../projects/ProjectBreadcrumb';
import { useProjectStore } from '@/frontend/store/ProjectStore';
import { ButtonParent } from '../buttons/ButtonParent';
import { ProductPageEditor } from './ProductPageEditor';
import { ProductPageContentType } from '@/backend/access/productPageContent/types';

export function ProductPageLoader(props: { type: ProductPageSectionType, editable: boolean }) {
  const { type, editable } = props;
  const { procedureId, procedureInstanceId, projectId } = useParams();
  const { fetchSections, fetchContents } = useProductPageStore();
  const fetchProcedureInstance = useProcedureInstanceStore(state => state.fetchProcedureInstance);
  const fetchProject = useProjectStore(state => state.fetchProject);
  const resetProcedureInstance = useProcedureInstanceStore(state => state.reset);
  const resetProductPage = useProductPageStore(state => state.reset);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (procedureInstanceId) {
        setLoading(true);
        const promises = [
          fetchSections(procedureInstanceId, type),
          fetchContents(procedureInstanceId, type),
          fetchProcedureInstance(procedureInstanceId),
        ];
        if(projectId) {
          promises.push(fetchProject(projectId));
        }
        await Promise.all(promises);
        setLoading(false);
      }
    };

    void fetchData();

    return () => {
      resetProcedureInstance();
      resetProductPage();
    };
  }, [procedureInstanceId]);

  if (!procedureInstanceId) {
    return <NotFound />;
  }

  if (loading) {
    return <ContainersPlaceholder />;
  }

  return <div className='overflow-auto' style={{ maxHeight: '100vh' }}><ProductPage type={type} editable={editable} procedureId={procedureId} /></div>;
}

export function ProductPage(props: { type: ProductPageSectionType, editable: boolean, procedureId?: string }) {
  const { type, editable, procedureId } = props;
  const { projectId } = useParams();
  const initializeProcedureFromTemplate = useProcedureListStore(state => state.initializeProcedureFromTemplate);
  const navigate = useNavigate();

  const sections = useProductPageStore((state) => state.sections);
  const [showEditor, setShowEditor] = useState(false);
  const procedureInstance = useProcedureInstanceStore(state => state.procedureInstance);
  const [loading, setLoading] = useState(false);

  if (!procedureInstance) {
    return <NotFound />;
  }

  const handleCreateProcedure = async () => {
    setLoading(true);
    try {
      const response = await initializeProcedureFromTemplate(procedureInstance.id, projectId);
      if (response.status === 200 && response.procedureInstance) {
        const url = `/procedureCreate/${response.procedureInstance.id}?projectId=${projectId}`;
        void navigate(url);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {projectId ? <ProjectBreadcrumb projectId={projectId} showProjectLink /> : <Breadcrumbs items={[
        { title: I18n.get('procedureTemplates'), link: '/procedure-templates' },
        { title: procedureInstance.title, link: `/procedures/${procedureId}` },
      ]} />}
      <div className='pt-3' style={{ width: '800px', margin: '0 auto' }}>
        <div className='procedure-details'>{I18n.get('typeDetails', { type: I18n.get(type) })}</div>
        <h4 className='card-title mb-3'>{procedureInstance.title}</h4>
        {!editable && type === ProductPageSectionType.TEMPLATE && <Button
            className='fusa-btn-small-with-icon'
            onClick={() => void handleCreateProcedure()}
            disabled={loading}
          >
            <Settings2 size={16} />
            {loading ? I18n.get('loading') : I18n.get('configureProcedure')}
          </Button> }
          {editable && <ButtonParent className='fusa-btn-small-with-icon btn-light d-flex gap-1 align-items-center' onClick={() => setShowEditor(true)} title={I18n.get('edit')}>
          <Pencil size={16} color='var(--gray-600)' />
          <span style={{ color: 'var(--gray-600)' }}>{I18n.get('edit')}</span>
        </ButtonParent>}
        { showEditor && <ProductPageEditor onHide={() => setShowEditor(false)} procedureInstanceId={procedureInstance.id} type={type} />}
        <div>
          {sections.map((section) => <ProductPageSectionItem key={section.id} section={section} />)}
        </div>
      </div>
    </div>
  );
}

function ProductPageSectionItem({ section }: { section: ProductPageSection; }) {
  const contents = useProductPageStore(state => state.contents)
    .filter(c => c.productPageSectionId === section.id);


  return (
    <div className='mt-4' key={section.id}>
      <div>
        <div className='product-page-section-title'>
          {section.title}
        </div>
        <p className='product-page-section-description'>{section.description}</p>
      </div>
      <div className='card-body'>
        {contents.map((content) => (
          <ContentItem key={content.id} content={content} />
        ))}
      </div>
    </div>
  );
}

function ContentItem({ content }: { content: ProductPageContent }) {
  return (
    <div className='mb-2'>
      <div className='d-flex justify-content-between align-items-start'>
        <div>
          {content.file && <FileWithDisplay file={content.file} hideFilePreview={false} />}
          {content.title && <div className='product-page-content-title'>{content.title}</div>}
          {content.description && <p
            className={`${content.type === ProductPageContentType.TEXT ? 'product-page-section-description' : 'text-sm-hint'}`}
            style={{ whiteSpace: 'pre-wrap' }}
          >
            {content.description}
          </p>}
        </div>
      </div>
    </div>
  );
}
